import { ChangeContext, ChangeOrigin } from '../client/changeManager';
import { ResourceClient } from '../client/types';
import { CacheEntry } from '../lifecycleManager/entries/cacheEntry';
import { ResourceLifecycleManager } from '../lifecycleManager/resourceLifecycleManager';
import { PersistenceManager } from '../persistence/persistenceManager';
import { PollManager } from '../polling/pollManager';
import { ResolverRegistry } from '../resolvers/registry/resolverRegistry';
import { ResourceRegistry } from '../resources';
import { checkDevtoolsEnabled } from './checkDevtoolsEnabled';
import { Log, Logger } from './logger';
import { TraceEntry, WatcherAttribution } from './deferred/messages';
import { LinkBehavior } from '../links/definition/link';

export type DevtoolsCallbacks = {
  onStateChange: (instanceKey: string, entry: CacheEntry<any, any>) => void;
  onLog: (log: Log) => void;
  onNodeCollected: (instanceKey: string) => void;
  onNodeAdded: (instanceKey: string, changeContext: ChangeContext) => void;
  onLinkAdded: (
    sourceKey: string,
    targetKey: string,
    outgoingBehavior: LinkBehavior,
    incomingBehavior: LinkBehavior,
    changeContext: ChangeContext
  ) => void;
  onLinkRemoved: (
    sourceKey: string,
    targetKey: string,
    changeContext: ChangeContext
  ) => void;
  onTagAdded: (
    instanceKey: string,
    linkKey: string,
    tag: string,
    changeContext: ChangeContext
  ) => void;
  onTagRemoved: (
    instanceKey: string,
    linkKey: string,
    tag: string,
    changeContext?: ChangeContext
  ) => void;
  traceOrigin: (
    origin: ChangeOrigin,
    changeContext: ChangeContext,
    stack?: string
  ) => void;
  trace: (entry: TraceEntry) => void;
  traceInit: (
    parentInstanceKey: string,
    childInstanceKey: string,
    changeContext: ChangeContext
  ) => void;
  onWatcherAdded: (
    watcherId: number,
    instanceKey: string,
    attribution: WatcherAttribution
  ) => void;
  onWatcherRemoved: (watcherId: number) => void;
};

export type Devtools = {
  devtoolsId: string | null;
  callbacks?: DevtoolsCallbacks;
  registerHooks: (args: {
    resourceRegistry: ResourceRegistry;
    resolverRegistry: ResolverRegistry;
    lifecycleManager: ResourceLifecycleManager;
    pollManager: PollManager;
    invalidate: ResourceClient['invalidate'];
    setState: ResourceClient['setState'];
    getPollGroupMembers: () => Record<string, string[]>;
    persistenceManager: PersistenceManager;
  }) => void;
};

const NOOP: Devtools = {
  devtoolsId: null,
  registerHooks: () => {},
};

export const maybeLoadDevtools = ({
  clientId,
  logger,
}: {
  clientId: string;
  logger?: Logger;
}): Devtools => {
  if (checkDevtoolsEnabled()) {
    let deferredRegisterArgs: any = null;
    let real: Devtools | null = null;

    const callbackQueue: {
      method: string;
      args: any[];
    }[] = [];

    const devtools: Devtools = {
      devtoolsId: clientId,
      callbacks: new Proxy(
        {},
        {
          get:
            (__, method: string) =>
            (...args: any[]) => {
              if (real) {
                (real.callbacks as any)?.[method]?.(...args);
              } else {
                callbackQueue.push({ method, args });
              }
            },
        }
      ) as DevtoolsCallbacks,
      registerHooks: (args: any) => {
        if (real) {
          real.registerHooks(args);
        } else {
          deferredRegisterArgs = args;
        }
      },
    };

    logger?.debug('loading devtools');

    import('./deferred/devtools')
      .then(({ createDevtools }) => {
        logger?.info('devtools chunk loaded');

        real = createDevtools({
          devtoolsId: clientId,
          logger,
        });

        // Should never happen, as register is called sync, but might as well
        // guard against it
        if (deferredRegisterArgs) {
          real.registerHooks(deferredRegisterArgs);
        }

        for (const { method, args } of callbackQueue) {
          (real.callbacks as any)?.[method]?.(...args);
        }

        // Clear the callback queue to release memory
        callbackQueue.length = 0;
      })
      .catch((err) => {
        console.error('Error loading devtools', err);
      });

    logger?.attachDevtools((log) => {
      devtools.callbacks?.onLog?.(log);
    });

    return devtools;
  }

  return NOOP;
};
